import React, { useEffect, useState } from 'react';
import { createRoute, redirect, Link } from '@tanstack/react-router';
import { useAuth0 } from '@auth0/auth0-react';
import { API_BASE_URL } from '@/client/config';
import { Route as rootRoute } from './__root';
import { Layout } from '@/client/layouts/layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/client/components/ui/card';
import { Database, Server, BookCheck } from 'lucide-react';
import { isLoginRequiredError } from '@/client/lib/apiError';

type ServerType = 'ingress' | 'egress' | 'transcode' | 'unknown';

interface EC2Instance {
  instanceId: string;
  state: string | null;
  serverType: ServerType;
}

interface RDSInstance {
  identifier: string;
  status: string;
}

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard',
  beforeLoad: async ({ context }) => {
    // Redirect to home if not authenticated
    if (!context.auth.isAuthenticated) {
      throw redirect({
        to: '/',
      });
    }
  },
  component: DashboardComponent,
});

function DashboardComponent() {
  const { getAccessTokenSilently, isAuthenticated, isLoading, logout } = useAuth0();
  const [upcomingCount, setUpcomingCount] = useState<number | null>(null);
  const [liveCount, setLiveCount] = useState<number | null>(null);
  const [onlineEncodersCount, setOnlineEncodersCount] = useState<number | null>(null);
  const [ec2Counts, setEc2Counts] = useState<{ total: number; ingress: number; egress: number; transcode: number } | null>(null);
  const [rdsAvailable, setRdsAvailable] = useState<number | null>(null);
  const [publishAvgs, setPublishAvgs] = useState<{
    day: { count: number; avgPerDay: number };
    week: { count: number; avgPerDay: number };
    month: { count: number; avgPerDay: number };
  } | null>(null);

  useEffect(() => {
    if (isLoading || !isAuthenticated) return;

    const fetchData = async () => {
      try {
        const token = await getAccessTokenSilently();
        const [upcomingRes, liveRes, encodersRes, ec2Res, rdsRes, publishRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/upcoming-streams?dateRange=24hrs`, {
            headers: { 'Authorization': `Bearer ${token}` },
          }),
          fetch(`${API_BASE_URL}/api/live-streams`, {
            headers: { 'Authorization': `Bearer ${token}` },
          }),
          fetch(`${API_BASE_URL}/api/encoders`, {
            headers: { 'Authorization': `Bearer ${token}` },
          }),
          fetch(`${API_BASE_URL}/api/aws/ec2/instances`, {
            headers: { 'Authorization': `Bearer ${token}` },
          }),
          fetch(`${API_BASE_URL}/api/aws/rds/instances`, {
            headers: { 'Authorization': `Bearer ${token}` },
          }),
          fetch(`${API_BASE_URL}/api/events/publish-averages`, {
            headers: { 'Authorization': `Bearer ${token}` },
          }),
        ]);
        const upcomingData = await upcomingRes.json();
        const liveData = await liveRes.json();
        const encodersData = await encodersRes.json();
        setUpcomingCount(upcomingData.upcomingStreams?.length ?? 0);
        setLiveCount(liveData.liveStreams?.length ?? 0);
        // Status 1 = Online
        const onlineCount = encodersData.encoders?.filter((e: { status: number }) => e.status === 1).length ?? 0;
        setOnlineEncodersCount(onlineCount);

        // AWS EC2 data
        if (ec2Res.ok) {
          const ec2Data: { instances: EC2Instance[] } = await ec2Res.json();
          const running = ec2Data.instances.filter(i => i.state === 'running');
          const counts = { total: 0, ingress: 0, egress: 0, transcode: 0 };
          running.forEach(i => {
            counts.total++;
            if (i.serverType === 'ingress') counts.ingress++;
            else if (i.serverType === 'egress') counts.egress++;
            else if (i.serverType === 'transcode') counts.transcode++;
          });
          setEc2Counts(counts);
        }

        // AWS RDS data
        if (rdsRes.ok) {
          const rdsData: { instances: RDSInstance[] } = await rdsRes.json();
          const available = rdsData.instances.filter(i => i.status === 'available').length;
          setRdsAvailable(available);
        }

        // Publish averages
        if (publishRes.ok) {
          setPublishAvgs(await publishRes.json());
        }
      } catch (err) {
        if (isLoginRequiredError(err)) {
          // Session expired - clear stale auth state and redirect to login
          logout({ logoutParams: { returnTo: window.location.origin } });
          return;
        }
        console.error('Error fetching dashboard data:', err);
      }
    };
    fetchData();
  }, [isLoading, isAuthenticated, getAccessTokenSilently, logout]);

  return (
    <Layout>
      <div>
        <h1 className="text-3xl font-bold mb-6">Dashboard</h1>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Link to="/events/live-streams">
            <Card className="cursor-pointer transition-colors hover:bg-accent py-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground text-center">
                  Live Streams
                </CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <div className="text-6xl font-bold py-4">
                  {liveCount !== null ? liveCount : '—'}
                </div>
                <p className="text-xs text-muted-foreground mt-1">Currently streaming</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/events/upcoming-streams">
            <Card className="cursor-pointer transition-colors hover:bg-accent py-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground text-center">
                  Upcoming Streams
                </CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <div className="text-6xl font-bold py-4">
                  {upcomingCount !== null ? upcomingCount : '—'}
                </div>
                <p className="text-xs text-muted-foreground mt-1">Next 24 hours</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/encoders">
            <Card className="cursor-pointer transition-colors hover:bg-accent py-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground text-center">
                  Online Encoders
                </CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <div className="text-6xl font-bold py-4">
                  {onlineEncodersCount !== null ? onlineEncodersCount : '—'}
                </div>
                <p className="text-xs text-muted-foreground mt-1">Currently online</p>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* AWS Health Section */}
        <h2 className="text-xl font-semibold mt-8 mb-4 flex items-center gap-2">
          <Server className="h-5 w-5" />
          AWS Health
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Link to="/aws/ec2-health">
            <div className="p-4 border rounded-lg hover:bg-accent transition-colors cursor-pointer">
              <div className="text-sm text-muted-foreground">EC2 Running</div>
              <div className="text-2xl font-bold">{ec2Counts?.total ?? '—'}</div>
            </div>
          </Link>
          <Link to="/aws/ec2-health">
            <div className="p-4 border rounded-lg hover:bg-accent transition-colors cursor-pointer">
              <div className="text-sm text-muted-foreground">Ingress</div>
              <div className="text-2xl font-bold text-blue-600">{ec2Counts?.ingress ?? '—'}</div>
            </div>
          </Link>
          <Link to="/aws/ec2-health">
            <div className="p-4 border rounded-lg hover:bg-accent transition-colors cursor-pointer">
              <div className="text-sm text-muted-foreground">Egress</div>
              <div className="text-2xl font-bold text-purple-600">{ec2Counts?.egress ?? '—'}</div>
            </div>
          </Link>
          <Link to="/aws/ec2-health">
            <div className="p-4 border rounded-lg hover:bg-accent transition-colors cursor-pointer">
              <div className="text-sm text-muted-foreground">Transcode</div>
              <div className="text-2xl font-bold text-orange-600">{ec2Counts?.transcode ?? '—'}</div>
            </div>
          </Link>
          <Link to="/aws/rds-health">
            <div className="p-4 border rounded-lg hover:bg-accent transition-colors cursor-pointer">
              <div className="text-sm text-muted-foreground flex items-center gap-1">
                <Database className="h-4 w-4" />
                RDS Available
              </div>
              <div className="text-2xl font-bold text-green-600">{rdsAvailable ?? '—'}</div>
            </div>
          </Link>
        </div>

        {/* Published Events Section */}
        <h2 className="text-xl font-semibold mt-8 mb-4 flex items-center gap-2">
          <BookCheck className="h-5 w-5" />
          Published Events
        </h2>
        <div className="grid grid-cols-3 gap-4">
          <div className="p-4 border rounded-lg">
            <div className="text-sm text-muted-foreground">Last 24 Hours</div>
            <div className="text-2xl font-bold">{publishAvgs?.day.count ?? '—'}</div>
            <div className="text-xs text-muted-foreground mt-1">events published</div>
          </div>
          <div className="p-4 border rounded-lg">
            <div className="text-sm text-muted-foreground">Last 7 Days</div>
            <div className="text-2xl font-bold">{publishAvgs?.week.count ?? '—'}</div>
            <div className="text-xs text-muted-foreground mt-1">
              avg {publishAvgs?.week.avgPerDay ?? '—'}/day
            </div>
          </div>
          <div className="p-4 border rounded-lg">
            <div className="text-sm text-muted-foreground">Last 30 Days</div>
            <div className="text-2xl font-bold">{publishAvgs?.month.count ?? '—'}</div>
            <div className="text-xs text-muted-foreground mt-1">
              avg {publishAvgs?.month.avgPerDay ?? '—'}/day
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
